import { useCallback, useState } from 'react';

import type { HandledStatusFilter } from '~/components/communications/types';

interface UseHandledStatusFilterConfig {
  isEnabled: boolean;
  defaultsToPending: boolean;
  isConfigLoading: boolean;
}

interface UseHandledStatusFilterReturn {
  handledStatusFilter: HandledStatusFilter;
  setHandledStatusFilter: (value: HandledStatusFilter) => void;
  resetHandledStatusFilter: () => void;
  isDefaultState: boolean;
  handledStatusParam: Exclude<HandledStatusFilter, 'all'> | undefined;
}

export const useHandledStatusFilter = ({
  isEnabled,
  defaultsToPending,
  isConfigLoading,
}: UseHandledStatusFilterConfig): UseHandledStatusFilterReturn => {
  const [userSelection, setUserSelection] = useState<HandledStatusFilter | null>(null);

  const defaultFilter: HandledStatusFilter = isEnabled && defaultsToPending ? 'pending' : 'all';

  // No filter is sent until the organization configuration resolves, and the organization default
  // applies only while the user has not chosen for themselves — so a configuration that arrives late
  // (or from cache, then fresh) can still take effect, while a deliberate choice always wins. The
  // 'all' is forced at the read rather than only at initialisation, so a configuration that turns
  // handled status off mid-session cannot leave a stale 'pending' driving the query.
  const handledStatusFilter: HandledStatusFilter = isEnabled
    ? (userSelection ?? (isConfigLoading ? 'all' : defaultFilter))
    : 'all';

  const setHandledStatusFilter = useCallback((value: HandledStatusFilter) => setUserSelection(value), []);

  // Clear-filters lands on 'all' rather than back on the organization default, so clearing can only
  // ever widen the list — returning to a 'pending' default would show fewer rows than before.
  const resetHandledStatusFilter = useCallback(() => setUserSelection('all'), []);

  const handledStatusParam = handledStatusFilter === 'all' ? undefined : handledStatusFilter;

  return {
    handledStatusFilter,
    setHandledStatusFilter,
    resetHandledStatusFilter,
    isDefaultState: handledStatusFilter === 'all',
    handledStatusParam,
  };
};
